import React, { useState, useEffect, useContext } from 'react';
import {
  Search,
  Package,
  Plus,
  RefreshCw,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  User,
  Tag,
  Hash,
  Box
} from 'lucide-react';
import InboundContext from './InboundContext';
import { Button, Input, Card, Badge, LoadingSpinner, Notification } from './SharedComponents';
import { openDB } from 'idb';

const API_URL = import.meta.env.VITE_API_URL;

function AddToShelf() {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [shelfCode, setShelfCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastReloadTime, setLastReloadTime] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const { selectedNickname } = useContext(InboundContext);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  useEffect(() => {
    fetchAllItems();
  }, []);

  useEffect(() => {
    if (!isLoading && searchTerm.length > 2) {
      setIsSearching(true);
      const timeoutId = setTimeout(() => {
        handleLocalSearch();
        setIsSearching(false);
      }, 300); // Debounce search
      return () => clearTimeout(timeoutId);
    } else {
      setFilteredResults([]);
      setIsSearching(false);
    }
  }, [searchTerm, isLoading]);

  const fetchAllItems = async (forceReload = false) => {
    try {
      setIsLoading(true);
      const db = await openDB('ShelfDB', 1, {
        upgrade(db) {
          db.createObjectStore('items', { keyPath: 'ID' });
          db.createObjectStore('meta', { keyPath: 'key' });
        },
      });

      const cachedItems = await db.getAll('items');
      const cachedTimestamp = await db.get('meta', 'cachedTimestamp');

      const cacheDuration = 3 * 60 * 60 * 1000; // 3 hours
      const isCacheValid = cachedItems.length > 0 && cachedTimestamp &&
        (Date.now() - cachedTimestamp.value < cacheDuration);

      if (isCacheValid && !forceReload) {
        console.log('Using cached data');
        setAllItems(cachedItems);
        setLastReloadTime(new Date(cachedTimestamp.value));
        setIsLoading(false);
        return;
      }

      showNotification('Loading fresh data, please wait...', 'info');

      const response = await fetch(`${API_URL}/api/search_items`);
      const data = await response.json();

      const transformedData = data.map(item => ({
        ID: item.ID,
        searchable: `${item.SKU} ${item.TITLE} ${item.ASIN} ${item.FNSKU} ${item.name}`.toLowerCase(),
        ...item,
      }));

      await db.clear('items');
      await db.put('meta', { key: 'cachedTimestamp', value: Date.now() });

      for (const item of transformedData) {
        await db.put('items', item);
      }

      setAllItems(transformedData);
      setLastReloadTime(new Date());
      setIsLoading(false);
      showNotification('Data loaded successfully');
    } catch (error) {
      console.error('Error fetching items:', error);
      setIsLoading(false);
      showNotification('Failed to load data', 'error');
    }
  };

  const handleLocalSearch = () => {
    const keywords = searchTerm.toLowerCase().split(' ').filter(term => term.length > 0);
    const results = allItems.filter(item =>
      keywords.every(keyword => item.searchable.includes(keyword))
    ).slice(0, 24); // Limit to 24 results for performance
    setFilteredResults(results);
  };

  const handleCardClick = (item) => {
    setSelectedItem(item);
    setDrawerOpen(true);
    setShelfCode('');
    setQuantity('');
  };

  const handleAddToShelf = async () => {
    if (!shelfCode || !quantity) {
      showNotification('Please fill in both shelf code and quantity', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/add_or_update_shelving_qty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: `${shelfCode}-${selectedItem.SKU}`,
          qty: parseInt(quantity),
          sku: selectedItem.SKU,
          shelfCode,
          employee: selectedNickname || 'SYSTEM',
          shipmentId: selectedItem.INBOUND_ID || 'N/A',
          action_type: 'INBOUND_TOOL_SEARCHANDADDTOSHELF',
          team_id: selectedItem.TEAM_ID
        }),
      });

      const data = await response.json();
      if (data.success) {
        showNotification(`Added ${quantity} of "${selectedItem.TITLE}" to shelf ${shelfCode}`);
        setDrawerOpen(false);
        setShelfCode('');
        setQuantity('');
      } else {
        showNotification('Failed to add item to shelf', 'error');
      }
    } catch (error) {
      console.error('Error adding item to shelf:', error);
      showNotification('Failed to add item to shelf', 'error');
    }
  };

  const getStockStatus = (qty) => {
    const stock = parseInt(qty) || 0;
    if (stock === 0) return { variant: 'error', label: 'Out of Stock', icon: AlertCircle };
    if (stock < 10) return { variant: 'warning', label: 'Low Stock', icon: AlertCircle };
    return { variant: 'success', label: 'In Stock', icon: CheckCircle2 };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Notification */}
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Add to Shelf</h1>
            <p className="text-gray-600">Search and add inventory items to warehouse shelves</p>
          </div>
          <div className="flex items-center space-x-4">
            {lastReloadTime && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="text-sm font-medium text-gray-900">
                  {lastReloadTime.toLocaleString()}
                </p>
              </div>
            )}
            <Button
              onClick={() => fetchAllItems(true)}
              variant="outline"
              disabled={isLoading}
              className="flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <Card className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                placeholder="Search by SKU, ASIN, FNSKU, product title, or client name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={Search}
                disabled={isLoading}
                className="text-base"
              />
            </div>
            <div className="flex items-center text-sm text-gray-500 whitespace-nowrap">
              {isLoading ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Loading data...
                </span>
              ) : isSearching ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Searching...
                </span>
              ) : filteredResults.length > 0 ? (
                `${filteredResults.length} results`
              ) : searchTerm.length > 2 ? (
                'No results found'
              ) : (
                `${allItems.length.toLocaleString()} items loaded`
              )}
            </div>
          </div>

          {searchTerm.length > 0 && searchTerm.length <= 2 && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Please enter at least 3 characters to search
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20">
          <LoadingSpinner size="lg" className="mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Inventory Data</h3>
          <p className="text-gray-500 text-center max-w-md">
            This may take a moment on first load. We're fetching the latest inventory information.
          </p>
        </div>
      )}

      {/* Search Results */}
      {!isLoading && searchTerm.length > 2 && (
        <div>
          {filteredResults.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredResults.map((item) => {
                const stockStatus = getStockStatus(item.QTY_IN_STOCK);
                return (
                  <Card
                    key={item.ID}
                    className="p-4 cursor-pointer hover:shadow-lg hover:border-gray-900 transition-all duration-200"
                    onClick={() => handleCardClick(item)}
                  >
                    {/* Product Image */}
                    <div className="aspect-square bg-gray-100 rounded-lg mb-4 overflow-hidden">
                      {item.LARGE_IMG ? (
                        <img
                          src={item.LARGE_IMG}
                          alt={item.TITLE}
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-medium text-gray-900 line-clamp-2 mb-2">
                          {item.TITLE}
                        </h3>
                        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                          <User className="w-4 h-4" />
                          <span>{item.name}</span>
                        </div>
                      </div>

                      {/* Stock Status */}
                      <div className="flex items-center justify-between">
                        <Badge variant={stockStatus.variant} className="flex items-center">
                          <stockStatus.icon className="w-3 h-3 mr-1" />
                          {stockStatus.label}
                        </Badge>
                        <span className="text-sm font-medium text-gray-900">
                          Qty: {item.QTY_IN_STOCK}
                        </span>
                      </div>

                      {/* Product Identifiers */}
                      <div className="space-y-1">
                        <div className="flex items-center text-xs text-gray-500">
                          <Tag className="w-3 h-3 mr-1" />
                          <span className="font-mono">{item.SKU}</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <Hash className="w-3 h-3 mr-1" />
                          <span className="font-mono">{item.ASIN}</span>
                        </div>
                        {item.INBOUND_ID && (
                          <div className="flex items-center text-xs text-gray-500">
                            <Box className="w-3 h-3 mr-1" />
                            <span>Inbound: {item.INBOUND_ID}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                No items match your search criteria. Try using different keywords or check your spelling.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && searchTerm.length === 0 && (
        <div className="text-center py-20">
          <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
            <Search className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">Ready to Search</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Start typing in the search box above to find inventory items and add them to warehouse shelves.
          </p>
          <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Search by SKU, ASIN, or title
            </div>
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Real-time inventory data
            </div>
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
              Quick shelf assignment
            </div>
          </div>
        </div>
      )}

      {/* Side Drawer */}
      {drawerOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end p-4 z-50">
          <div className="bg-white w-full max-w-md h-full rounded-l-lg shadow-xl flex flex-col">
            {/* Drawer Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-200">
              <div className="flex-1 pr-4">
                <h2 className="text-lg font-semibold text-gray-900 line-clamp-2 mb-2">
                  {selectedItem.TITLE}
                </h2>
                <p className="text-sm text-gray-500">{selectedItem.name}</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => setDrawerOpen(false)}
                className="p-2 -mr-2"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Product Image */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                {selectedItem.LARGE_IMG ? (
                  <img
                    src={selectedItem.LARGE_IMG}
                    alt={selectedItem.TITLE}
                    className="w-full h-48 object-contain"
                  />
                ) : (
                  <div className="w-full h-48 flex items-center justify-center">
                    <Package className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">Stock Quantity</p>
                    <p className="font-medium">{selectedItem.QTY_IN_STOCK}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">Status</p>
                    <Badge variant={getStockStatus(selectedItem.QTY_IN_STOCK).variant}>
                      {getStockStatus(selectedItem.QTY_IN_STOCK).label}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-gray-500 text-sm mb-1">SKU</p>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                      {selectedItem.SKU}
                    </code>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">ASIN</p>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                      {selectedItem.ASIN}
                    </code>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">FNSKU</p>
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">
                      {selectedItem.FNSKU}
                    </code>
                  </div>
                  {selectedItem.INBOUND_ID && (
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Inbound ID</p>
                      <p className="text-sm">{selectedItem.INBOUND_ID}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Add to Shelf Form */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Add to Shelf</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shelf Code
                  </label>
                  <Input
                    placeholder="e.g., P-01-01"
                    value={shelfCode}
                    onChange={(e) => setShelfCode(e.target.value.toUpperCase())}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity
                  </label>
                  <Input
                    type="number"
                    placeholder="Enter quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="border-t border-gray-200 p-6">
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setDrawerOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddToShelf}
                  disabled={!shelfCode || !quantity}
                  className="flex-1 flex items-center justify-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Shelf
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddToShelf;